
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Center, Loader, Text } from "@mantine/core";
import {
  getShiftManagementUsers,
  bulkUpdateWorkingShift,
  exportShiftUsers
} from "../../../api/shiftManagement";
import { 
  getWorkingShifts, 
  createWorkingShift, 
  updateWorkingShift,
  toggleWorkingShiftActive,
} from "../../../api/WorkingShiftAPI";
//Added To Export Related Import
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { assignEmployeeShiftAPI, getTemporaryShiftUsersAPI } from "../../../api/EmployeeShiftAPI";


/* TYPES  */

type ShiftUser = {
  id: string;
  userName: string;
  employeeCode: string;
  workShift: string;
};

type Shift = {
  id: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
};

/* CONSTANTS  */

const EMPLOYEE_AVATAR =
  "https://www.gstatic.com/images/branding/product/1x/avatar_circle_grey_512dp.png";

const getShiftDurationError = (startTime: string, endTime: string): string | null => {
  const parse = (value: string) => {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
  };
  const start = parse(startTime);
  const rawEnd = parse(endTime);
  if (start === null || rawEnd === null) return "Please enter valid shift start and end times.";
  if (start === rawEnd) return "Shift start and end times must be different.";
  const end = rawEnd < start ? rawEnd + 24 * 60 : rawEnd;
  return end - start > 10 * 60 ? "Shift duration cannot exceed 10 hours." : null;
};

/*  COMPONENT  */

const ShiftManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ShiftUser[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);


  const[newShift, setNewShift] = useState({
    shiftName: "",
    startTime: "",
    endTime: "",
  });

  // Row-wise update
  const [selectedShiftMap, setSelectedShiftMap] =
    useState<Record<string, string>>({});

  // Bulk update
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkShiftId, setBulkShiftId] = useState("");

  //Temp Shift
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  //Add Shift Modal(Shift Master)
  const [showAddShift, setShowAddShift ] = useState(false);
  const [savingShift, setSavingShift] = useState(false);
  //Shift Master
  //const [activeTab, setActiveTab] = useState<"SHIFT_MANAGEMENT" | "SHIFT_MASTER" |"TEMP_SHIFT_ASSIGN">("SHIFT_MANAGEMENT");
//   const [activeTab, setActiveTab] =
// useState<
//   "SHIFT_MANAGEMENT" |
//   "SHIFT_MASTER" |
//   "TEMP_SHIFT_ASSIGN" |
//   "TEMP_SHIFT_RECORDS"
// >(
//   (
//     localStorage.getItem(
//       "SHIFT_ACTIVE_TAB"
//     ) as
//       | "SHIFT_MANAGEMENT"
//       | "SHIFT_MASTER"
//       | "TEMP_SHIFT_ASSIGN"
//       | "TEMP_SHIFT_RECORDS"
//   ) || "SHIFT_MANAGEMENT"
// );

//Default to Shift Management tab, if localStorage value is not valid
  const [activeTab, setActiveTab] =
    useState<
      "SHIFT_MANAGEMENT" |
      "SHIFT_MASTER" |
      "TEMP_SHIFT_ASSIGN" |
      "TEMP_SHIFT_RECORDS"
    >("SHIFT_MANAGEMENT");
  //Editing Shift(Shift Master)
  const [editingShiftId, setEditingShiftId] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* EFFECTS */

  // Initial load
  useEffect(() => {
    if (activeTab === "SHIFT_MANAGEMENT" ||
      activeTab === "TEMP_SHIFT_ASSIGN" ||
      activeTab === "TEMP_SHIFT_RECORDS"
    ) {
      fetchActiveShifts();   // only active
    } else {
      fetchAllShifts();      // active + inactive
    }
  }, [activeTab]);

  // Search debounce
  // useEffect(() => {
  //   const t = setTimeout(() => {
  //     fetchUsers();
  //   }, 400);
  //   return () => clearTimeout(t);
  // }, [search, activeTab,
  //     page,
  //     rowsPerPage]);
  // Search debounce only
  useEffect(() => {

    const t = setTimeout(() => {
      fetchUsers();
    }, 400);

    return () => clearTimeout(t);

  }, [search]);

  // Normal fetch
  useEffect(() => {

    fetchUsers();

  }, [
    activeTab,
    page,
    rowsPerPage
  ]);

  useEffect(() => {

  localStorage.setItem(
    "SHIFT_ACTIVE_TAB",
    activeTab
  );

  }, [activeTab]);
  

  /*  API  */

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (
        activeTab === "TEMP_SHIFT_ASSIGN"
      ) {
        res =
          await getTemporaryShiftUsersAPI(
            page,
            rowsPerPage,
            search
          );

        if (res?.success) {
          setUsers(res.data.items || []);
          setTotalRecords(res.data.total || 0);
        } else {
          setError(
            res?.message ||
            "Failed to load users"
          );
        }
      }

      // ONLY TEMP ASSIGNED USERS
      else if (
        activeTab === "TEMP_SHIFT_RECORDS"
      ) {
        res =
          await getTemporaryShiftUsersAPI(
            page,
            rowsPerPage,
            search,
            true
          );

        if (res?.success) {
          setUsers(res.data.items || []);
          setTotalRecords(res.data.total || 0);
        } else {
          setError(
            res?.message ||
            "Failed to load users"
          );
        }
      }

      else {
        res =
          await getShiftManagementUsers(
            page,
            rowsPerPage,
            search
          );
        if (res?.statusCode === 200) {
          setUsers(res.data.items || []);
        } else {
          setError(
            res?.message ||
            "Failed to load users"
          );
        }
      }

      // reset selections
      // setSelectedUserIds([]);
      // setSelectedShiftMap({});
      // setBulkShiftId("");
          } catch (err) {
            setError("Something went wrong");
          } finally {
            setLoading(false);
          }
        };
  
      // For Shift Management (ACTIVE ONLY)
        const fetchActiveShifts = async () => {
          const res = await getWorkingShifts(true);
          if (res?.statusCode === 200) {
            setShifts(res.data || []);
          }
        };

        // For Shift Master (ALL shifts)
        const fetchAllShifts = async () => {
          const res = await getWorkingShifts(false);
          if (res?.statusCode === 200) {
            setShifts(res.data || []);
          }
        };

      /*  HANDLERS  */

      const handleSaveShift = async (userId: string) => {
        const shiftId = selectedShiftMap[userId];
        if (!shiftId) return;

        const res = await bulkUpdateWorkingShift([userId], shiftId);
        if (res?.statusCode === 200) {
          fetchUsers();
        }
      };

      const handleBulkShiftUpdate = async () => {
        if (!bulkShiftId || selectedUserIds.length === 0) return;

        const res = await bulkUpdateWorkingShift(selectedUserIds, bulkShiftId);
        if (res?.statusCode === 200) {
          fetchUsers();
        }
      };

      const handleAssignShift = async (
              userId: string
          ) => {
              const shiftId = selectedShiftMap[userId];
              if (!shiftId || !effectiveFrom) {
                  alert(
                      "Select shift and effective from date"
                  );
                  return;
              }
      
          try {
              const payload = {
                  employeeId: userId,
                  shiftId,
                  effectiveFrom,
                  effectiveTo:
                  effectiveTo || null,
                  notes:
                  "Temporary Shift Assignment"
              }
              const res =
                  await assignEmployeeShiftAPI(
                  payload
                  );
              if (res?.success) {
      
                  alert(
                  "Shift assigned successfully"
                  );
                  await fetchUsers();
                  setSelectedShiftMap((prev) => ({
                  ...prev,
                  [userId]: ""
                }));
              }
              } catch (error) {
              console.error(error);
              alert(
                  "Failed to assign shift"
              );
              }
          };
        //-------------------------------------------------------//
        const handleExport = async () => {
          try {
            const res = await exportShiftUsers();

            if (res?.statusCode !== 200) {
              alert("Failed to export data");
              return;
            }
            const data = res.data.items || [];
            const formattedData = data.map((u: any) => ({
              "Employee Code": u.employee_code,
              "Employee Name": `${u.first_name} ${u.last_name}`,
              "Shift Name": u.shift_name,
              "Start Time": u.start_time,
              "End Time": u.end_time,
            }));

            // your same code
            const worksheet = XLSX.utils.json_to_sheet(formattedData);

            // Add Title at top
            XLSX.utils.sheet_add_aoa(
              worksheet,
              [["Employee Shift Data"]],
              { origin: "A1" }
            );
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Shift Management");
            const excelBuffer = XLSX.write(workbook, {
              bookType: "xlsx",
              type: "array",
            });
            const blob = new Blob([excelBuffer], {
              type: "application/octet-stream",
            });

            saveAs(blob, "ShiftManagement.xlsx");

          } catch (error) {
            console.error("Export error:", error);
            alert("Export failed");
          }
        };

        const handleExportTempShiftAssign = async () => {
          try {
            const res = await getTemporaryShiftUsersAPI(
              1,
              100000,
              search
            );
            const exportData = (res?.data?.items || []).map((u: any) => ({
              "Employee Code": u.employeeCode,
              "Employee Name": u.userName,
              "Current Shift": u.workShift || "-",
            }));
            const worksheet =
              XLSX.utils.json_to_sheet(exportData);
            worksheet["!cols"] = [
              { wch: 20 },
              { wch: 30 },
              { wch: 35 },
            ];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
              workbook,
              worksheet,
              "Temporary Shift Assignment"
            );
            const excelBuffer = XLSX.write(workbook, {
              bookType: "xlsx",
              type: "array",
            });
            const blob = new Blob([excelBuffer], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, "TemporaryShiftAssignment.xlsx");
          } catch (error) {
            console.error("Export error:", error);
            alert("Export failed");
          }
        };
        
        //Handle Export for Temporary Shift Records
        const handleExportTempShiftRecords = async () => {
          try {
            const res = await getTemporaryShiftUsersAPI(
            1,
            100000,
            search,
            true
          );
          const exportData = (res?.data?.items || [])
            .filter((u: any) => u.shiftName !== "-")
            .map((u: any) => ({
              "Employee Code": u.employeeCode,
              "Employee Name": u.userName,
              "Shift Name": u.shiftName,
              "Start Date": u.effectiveFrom
                ? new Date(u.effectiveFrom).toLocaleDateString()
                : "-",
              "End Date": u.effectiveTo
                ? new Date(u.effectiveTo).toLocaleDateString()
                : "-",
            }));
            const worksheet =
              XLSX.utils.json_to_sheet(exportData);
              worksheet["!cols"] = [
                { wch: 18 }, // Employee Code
                { wch: 30 }, // Employee Name
                { wch: 25 }, // Shift Name
                { wch: 18 }, // Start Date
                { wch: 18 }, // End Date
              ];
            XLSX.utils.sheet_add_aoa(
              worksheet,
              [["Employee Code"]],
              { origin: "A1" }
            );
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
              workbook,
              worksheet,
              "Temp Shift Records"
            );
            const excelBuffer = XLSX.write(workbook, {
              bookType: "xlsx",
              type: "array",
            });
            const blob = new Blob([excelBuffer], {
              type: "application/octet-stream",
            });
            saveAs(blob, "TemporaryShiftRecords.xlsx");
          } catch (error) {
            console.error("Export error:", error);
            alert("Export failed");
          }
        };
          
        //-------------------------------------------------------//
        //Editing Working Shift(Shift Master)
        const handleEditShift = (shift : Shift) => {
          setEditingShiftId(shift.id);
          setNewShift({
            shiftName: shift.shiftName,
            startTime: shift.startTime,
            endTime: shift.endTime,
          });
          setShowAddShift(true);
        }

      // Create / Save Handler (Shift Master)
      const handleSaveShiftMaster = async () => {
      if (!newShift.shiftName || !newShift.startTime || !newShift.endTime) {
        alert("All fields are required");
        return;
      }

      const durationError = getShiftDurationError(newShift.startTime, newShift.endTime);
      if (durationError) {
        alert(durationError);
        return;
      }

      setSavingShift(true);
        try {
          let res;

          if (editingShiftId) {
            // UPDATE SHIFT
            res = await updateWorkingShift(editingShiftId, {
              shiftName: newShift.shiftName,
              startTime: newShift.startTime,
              endTime: newShift.endTime,
            });
          } else {
            // CREATE SHIFT
            res = await createWorkingShift({
              shiftName: newShift.shiftName,
              startTime: newShift.startTime,
              endTime: newShift.endTime,
              isActive: true,
            });
          }

          if (res?.statusCode === 200 || res?.statusCode === 201) {
            setShowAddShift(false);
            setEditingShiftId(null);
            setNewShift({ shiftName: "", startTime: "", endTime: "" });

            if (activeTab === "SHIFT_MANAGEMENT") {
              fetchActiveShifts();
            } else {
              fetchAllShifts();
            }
          }else{
            alert(res?.message || "Failed to save shift");
          }
        }catch (error: any) {
        alert(error?.response?.data?.message || error?.message || "Something went wrong");
      } finally {
        setSavingShift(false);
      }
    };


  // Active Inactive Status(Shift Master)
    const handleToggleShiftStatus = async (shiftId: string) => {
    try {
      const res = await toggleWorkingShiftActive(shiftId);

      if (res?.statusCode === 200) {
        fetchAllShifts(); // refresh Shift Master list
      } else {
        alert(res?.message || "Failed to update status");
      }
    } catch (error) {
      alert("Something went wrong while updating status");
    }
  };






  /*  PAGINATION  */

  // const total = users.length;
  const total =
  activeTab === "TEMP_SHIFT_ASSIGN" ||
  activeTab === "TEMP_SHIFT_RECORDS"
    ? totalRecords
    : users.length;

  // const paginatedUsers = users.slice(
  //   (page - 1) * rowsPerPage,
  //   page * rowsPerPage
  // );
  const paginatedUsers =
  activeTab === "TEMP_SHIFT_ASSIGN" ||
  activeTab === "TEMP_SHIFT_RECORDS"
    ? users
    : users.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
      );

  const allSelected =
    paginatedUsers.length > 0 &&
    paginatedUsers.every((u) => selectedUserIds.includes(u.id));

  const isBulkMode = selectedUserIds.length > 0;

  /*  UI  */

  return (
  <div className="card mt-4">
    <div className="card-body">

      {/* ================== Tab Header ======================= */}
      <div className="d-flex align-items-center gap-4 mb-4 border-bottom pb-2">
        <button
          className={`btn btn-link fw-bold px-2 py-4 fs-1 ${
            activeTab === "SHIFT_MANAGEMENT"
              ? "text-primary border-bottom border-7"
              : "text-muted"
          }`}
          onClick={() => setActiveTab("SHIFT_MANAGEMENT")}
        >
          Shift Managements
        </button>

          
        <button
          className={`btn btn-link fw-bold px-3 py-2 fs-7 ${
            activeTab === "SHIFT_MASTER"
              ? "text-primary border-bottom border-2"
              : "text-muted"
          }`}
          onClick={() => setActiveTab("SHIFT_MASTER")}
        >
          Shift Master
        </button>

        <button
          className={`btn btn-link fw-bold px-3 py-2 fs-7 ${
            activeTab === "TEMP_SHIFT_ASSIGN"
              ? "text-primary border-bottom border-2"
              : "text-muted"
          }`}
           onClick={() => setActiveTab("TEMP_SHIFT_ASSIGN")}
        >
          <i className="fas fa-exchange-alt me-2"></i>
          Temporary Shift Assignment
        </button>

        <button
          className={`btn btn-link fw-bold px-3 py-2 fs-7 ${
            activeTab === "TEMP_SHIFT_RECORDS"
              ? "text-primary border-bottom border-2"
              : "text-muted"
          }`}
          onClick={() =>
            setActiveTab("TEMP_SHIFT_RECORDS")
          }
        >
          <i className="fas fa-list me-2"></i>

          Temporary Shift Records
        </button>
      </div>

      {/* ============= Shift Management =================== */}
      {activeTab === "SHIFT_MANAGEMENT" && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-2">
              <input
                className="form-control form-control-sm"
                style={{ width: 220 }}
                placeholder="Search employee..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                className="form-select form-select-sm"
                style={{ width: 220 }}
                value={bulkShiftId}
                disabled={!isBulkMode}
                onChange={(e) => setBulkShiftId(e.target.value)}
              >
                <option value="">Select Shift</option>
                {shifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.shiftName} ({s.startTime}-{s.endTime})
                  </option>
                ))}
              </select>

              <button
                className="btn btn-sm btn-warning"
                disabled={!bulkShiftId || !isBulkMode}
                onClick={handleBulkShiftUpdate}
              >
                <i className="fas fa-exchange-alt me-2"></i>
                Change Shift ({selectedUserIds.length})
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={handleExport}
              >
                <i className="fas fa-file-excel me-2"></i>
                Export to Excel
              </button>
            </div>
          </div>
            {/* Loading */}
            {loading && 
              <Center>
                <Loader />
              </Center>
            }
            {/* Error */}
            {error && !loading && 
              <Center>
                <Text color="red">{error}</Text>
              </Center>
            }

            {/* Table */}
            {!loading && !error && (
              <>
                <table className="table align-middle">
                  <thead>
                    <tr className="fw-bold text-uppercase text-muted">
                      <th style={{ width: 50 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) =>
                            setSelectedUserIds(
                              e.target.checked
                                ? paginatedUsers.map((u) => u.id)
                                : []
                            )
                          }
                        />
                      </th>
                      <th>Employee Code</th>
                      <th>Name</th>
                      <th>Work Shift</th>
                      <th>Update Shift</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedUsers.length > 0 ? (
                      paginatedUsers.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(u.id)}
                              onChange={(e) =>
                                setSelectedUserIds(
                                  e.target.checked
                                    ? [...selectedUserIds, u.id]
                                    : selectedUserIds.filter((id) => id !== u.id)
                                )
                              }
                            />
                          </td>

                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={EMPLOYEE_AVATAR}
                                alt="employee"
                                width={32}
                                height={32}
                                className="rounded-circle me-2"
                              />
                              {u.employeeCode}
                            </div>
                          </td>

                          <td>{u.userName}</td>

                          <td className="text-muted">{u.workShift}</td>

                          <td>
                            <div className="d-flex gap-2">
                              <select
                                className="form-select form-select-sm"
                                style={{ width: 200 }}
                                disabled={isBulkMode}
                                value={selectedShiftMap[u.id] || ""}
                                onChange={(e) =>
                                  setSelectedShiftMap({
                                    ...selectedShiftMap,
                                    [u.id]: e.target.value,
                                  })
                                }
                              >
                                <option value="">Select Shift</option>
                                {shifts.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.shiftName}
                                  </option>
                                ))}
                              </select>

                              {selectedShiftMap[u.id] && !isBulkMode && (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleSaveShift(u.id)}
                                >
                                  Save
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No employees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    {(page - 1) * rowsPerPage + 1} –{" "}
                    {Math.min(page * rowsPerPage, total)} of {total}
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 80 }}
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={150}>150</option>
                      <option value={200}>200</option>
                    </select>

                    <button
                      className="btn btn-sm btn-light"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      ‹
                    </button>

                    <button className="btn btn-sm btn-primary">{page}</button>

                    <button
                      className="btn btn-sm btn-light"
                      disabled={page * rowsPerPage >= total}
                      onClick={() => setPage(page + 1)}
                    >
                      ›
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
          )}

          {/* =========== ShiftMaster Code ===================== */}

          {activeTab === "SHIFT_MASTER" && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3 className="mb-0">Shift Master</h3>
              <button className="btn btn-sm btn-primary" onClick={() => setShowAddShift(true)}>
                + Add Shift</button>
            </div>

            <table className="table align-middle">
              <thead>
                <tr className="fw-bold text-uppercase text-muted">
                  <th>Shift Name</th>
                  <th>Start Shift</th>
                  <th>End Shift</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {shifts.length > 0 ? (
                  shifts.map((s) => (
                    <tr key={s.id}>
                      <td>{s.shiftName}</td>
                      <td>{s.startTime}</td>
                      <td>{s.endTime}</td>
                     <td>
                        <span
                          className={`badge ${
                            s.isActive ? "bg-success" : "bg-secondary"
                          }`}
                        >
                          {s.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="d-flex gap-2">
                      {/* Editing Shift */}
                      <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditShift(s)}>
                          Edit Shift
                      </button>

                      {/* Activate / Deactivate */}
                      <button
                        className={`btn btn-sm ${
                          s.isActive ? "btn-outline-danger" : "btn-outline-success"
                        }`}
                        onClick={() => handleToggleShiftStatus(s.id)}
                      >
                        {s.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      No shifts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </>
          )}

          {/* Added Shift - Shift Master */}
          {showAddShift && (
            <div className="modal fade show d-block" style = {{background: "rgba(0,0,0,0.5)"}}
            >
              <div className="modal-dialog modal-md">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Add Shift</h5>
                    <button
                        className="btn-close"
                        onClick={() => {
                          setShowAddShift(false);
                          setEditingShiftId(null);
                          setNewShift({ shiftName: "", startTime: "", endTime: "" });
                        }}
                      />
                  </div>
                   <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Shift Name</label>
                      <input
                        className="form-control"
                        value={newShift.shiftName}
                        onChange={(e) =>
                          setNewShift({ ...newShift, shiftName: e.target.value })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Start Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={newShift.startTime}
                        onChange={(e) =>
                          setNewShift({ ...newShift, startTime: e.target.value })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">End Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={newShift.endTime}
                        onChange={(e) =>
                          setNewShift({ ...newShift, endTime: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddShift(false);
                        setEditingShiftId(null);
                        setNewShift({ shiftName: "", startTime: "", endTime: "" });
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-primary"
                      disabled={savingShift}
                      onClick={handleSaveShiftMaster}
                    >
                      {savingShift ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}


          {activeTab === "TEMP_SHIFT_ASSIGN" && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>
                  Employee Shift Assignment
                </h3>
                <div className="d-flex gap-2">
                  
                  {/* Search */}
                  <input
                    className="form-control form-control-sm"
                    style={{ width: 200,
                      height: "38px",
                     }}
                    placeholder="Search employee..."
                    value={search}
                    onChange={(e) =>
                      setSearch(e.target.value)
                    }
                  />

                  {/* Bulk Shift Select */} 
                  <select className="form-select form-select-sm" 
                    style={{ width: 200, height: "38px" }} 
                    value={bulkShiftId} 
                    disabled={selectedUserIds.length === 0} 
                    onChange={(e) => setBulkShiftId(e.target.value) } > 
                    <option value=""> 
                      Select Shift 
                    </option> 
                    {shifts.map((s) => ( 
                      <option key={s.id} value={s.id} >
                        {s.shiftName} {" "} ( {s.startTime} - {s.endTime} ) 
                      </option> 
                    ))} 
                  </select>

                  {/* Effective From */}
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ width: 150, height: "38px" }}
                    value={effectiveFrom}
                    onChange={(e) =>
                      setEffectiveFrom(
                        e.target.value
                      )
                    }
                  />

                  {/* Effective To */}
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ width: 150, height: "38px" }}
                    value={effectiveTo}
                    onChange={(e) =>
                      setEffectiveTo(
                        e.target.value
                      )
                    }
                  />

                  {/* Bulk Assign Button */} 
                  <button 
                    className="btn btn-sm btn-warning" 
                    disabled={ 
                      !bulkShiftId || 
                      selectedUserIds.length === 0 || 
                      !effectiveFrom 
                    } onClick={async () => { 
                      try { 
                        for (const userId of selectedUserIds) { 
                          await assignEmployeeShiftAPI({ 
                            employeeId: userId, 
                            shiftId: bulkShiftId, 
                            effectiveFrom, 
                            effectiveTo: effectiveTo || null, 
                            notes: 
                              "Bulk Temporary Shift Assignment" 
                          }); 
                        } 
                        alert( 
                          "Bulk temporary shift assigned successfully" 
                        ); 
                        fetchUsers(); 
                        setSelectedUserIds([]); 
                        setBulkShiftId(""); 
                        setSelectedShiftMap({});
                      } 
                      catch (error) { 
                        console.error(error); 
                        alert( 
                          "Failed to assign bulk shift" 
                          ); 
                        } 
                      }} 
                    > 
                    <i className="fas fa-exchange-alt me-2"></i> 
                    Assign Shift 
                    ( 
                      {selectedUserIds.length} 
                      ) 
                  </button>
                  
                  {/* Reset */}
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{
                      minWidth: "50px",
                      height: "38px",
                      whiteSpace: "nowrap"
                    }}
                    onClick={() => {
                      setSearch("");
                      setEffectiveFrom("");
                      setEffectiveTo("");
                      setSelectedUserIds([]);
                      setBulkShiftId("");
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
              <div className="d-flex justify-content-end mb-3">
                <button
                    className="btn btn-sm btn-success"
                    style={{
                      minWidth: "140px",
                      height: "38px",
                      whiteSpace: "nowrap"
                    }}
                    onClick={handleExportTempShiftAssign}
                  >
                    <i className="fas fa-file-excel me-2"></i>
                    Export to Excel
                  </button>
               </div>
              {/* LOADING */}
              {loading && (
                <Center>
                  <Loader />
                </Center>
              )}

              {/* ERROR */}
              {error && !loading && (
                <Center>
                  <Text color="red">
                    {error}
                  </Text>
                </Center>
              )}

              {/* TABLE */}
              {!loading && !error && (
                <>
                  <table className="table align-middle">
                    <thead>
                      <tr className="fw-bold text-uppercase text-muted">
                        {/* <th></th> */}
                        <th style={{ width: 50 }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) =>
                              setSelectedUserIds(
                                e.target.checked
                                  ? paginatedUsers.map((u) => u.id)
                                  : []
                              )
                            }
                          />
                        </th>
                        <th>Employee Code</th>
                        <th>Name</th>
                        <th>Current Shift</th>
                        <th>Assign Shift</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    
                    <tbody>
                      {paginatedUsers.length > 0 ? (
                        paginatedUsers.map((u) => (
                          <tr key={u.id}>
                            {/* Checkbox */} 
                            <td> 
                              <input type="checkbox" 
                                checked={selectedUserIds.includes(u.id)} 
                                onChange={(e) => setSelectedUserIds( 
                                  e.target.checked 
                                    ? [...selectedUserIds, u.id] 
                                    : selectedUserIds.filter( 
                                      (id) => id !== u.id 
                                      ) 
                                    ) 
                                  } 
                                /> 
                            </td>
                            {/* Employee */}
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={EMPLOYEE_AVATAR}
                                  alt="employee"
                                  width={32}
                                  height={32}
                                  className="rounded-circle me-2"
                                />
                                {u.employeeCode}
                              </div>
                            </td>

                            {/* Name */}
                            <td>
                              {u.userName}
                            </td>

                            {/* Current Shift */}
                            <td className="text-muted">
                              {u.workShift}
                            </td>
                            
                            {/* Assign Shift Dropdown */}
                            <td>
                              <select
                                className="form-select form-select-sm"
                                style={{ width: 220 }}
                                disabled={selectedUserIds.length > 0}
                                value={
                                  selectedShiftMap[u.id] || ""
                                }
                                onChange={(e) =>
                                  setSelectedShiftMap({
                                    ...selectedShiftMap,
                                    [u.id]:
                                      e.target.value,
                                  })
                                }
                              >
                                <option value="">
                                  Select Shift
                                </option>

                                {shifts.map((s) => (
                                  <option
                                    key={s.id}
                                    value={s.id}
                                  >
                                    {s.shiftName}
                                    {" "}
                                    (
                                    {s.startTime}
                                    -
                                    {s.endTime}
                                    )
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* Single Assign */}
                            <td>
                              <button
                                className="btn btn-sm btn-primary"
                                disabled={selectedUserIds.length > 0}
                                onClick={() =>
                                  handleAssignShift(u.id)
                                }
                              >
                                Assign Shift
                              </button>
                            </td>
                          </tr>
                        ))

                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted"
                          >
                            No employees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    {(page - 1) * rowsPerPage + 1} –{" "}
                    {Math.min(page * rowsPerPage, total)} of {total}
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 80 }}
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={150}>150</option>
                      <option value={200}>200</option>
                    </select>

                    <button
                      className="btn btn-sm btn-light"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      ‹
                    </button>

                    <button className="btn btn-sm btn-primary">{page}</button>

                    <button
                      className="btn btn-sm btn-light"
                      disabled={page * rowsPerPage >= total}
                      onClick={() => setPage(page + 1)}
                    >
                      ›
                    </button>
                  </div>
                </div>
                </>
              )}
            </>
          )}

          {activeTab === "TEMP_SHIFT_RECORDS" && (
            <>
              {/* <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>
                  Temporary Shift Records
                </h3>
                <input
                  className="form-control form-control-sm"
                  style={{ width: 220 }}
                  placeholder="Search employee..."
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                />
              </div> */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Temporary Shift Records</h3>

                <div className="d-flex gap-2">
                  <input
                    className="form-control form-control-sm"
                    style={{ width: 220 }}
                    placeholder="Search employee..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />

                  <button
                    className="btn btn-sm btn-success"
                    onClick={handleExportTempShiftRecords}
                  >
                    <i className="fas fa-file-excel me-2"></i>
                    Export to Excel
                  </button>
                </div>
              </div>

              <table className="table align-middle">
                <thead>
                  <tr className="fw-bold text-uppercase text-muted">
                    <th>Employee Code</th>
                    <th>Employee Name</th>
                    <th>Shift Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers
                      .filter((u: any) => u.shiftName !== "-")
                      .map((u: any) => (
                        <tr key={u.id}>
                        <td>
                          {u.employeeCode}
                        </td>

                        <td>
                          {u.userName}
                        </td>

                        <td>
                          {u.shiftName}
                        </td>

                        <td>
                          {u.effectiveFrom
                            ? new Date(
                                u.effectiveFrom
                              ).toLocaleDateString()
                            : "-"
                          }
                        </td>

                        <td>
                          {u.effectiveTo
                            ? new Date(
                                u.effectiveTo
                              ).toLocaleDateString()
                            : "-"
                          }
                        </td>
                      </tr>
                    ))

                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="text-center text-muted"
                      >
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    {(page - 1) * rowsPerPage + 1} –{" "}
                    {Math.min(page * rowsPerPage, total)} of {total}
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 80 }}
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={150}>150</option>
                      <option value={200}>200</option>
                    </select>

                    <button
                      className="btn btn-sm btn-light"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      ‹
                    </button>

                    <button className="btn btn-sm btn-primary">{page}</button>

                    <button
                      className="btn btn-sm btn-light"
                      disabled={page * rowsPerPage >= total}
                      onClick={() => setPage(page + 1)}
                    >
                      ›
                    </button>
                  </div>
                </div>
            </>
          )}
        </div>
      </div>
    );
};


            

export default ShiftManagement;
